import { useCallback, useEffect, useMemo, useState } from "react";
import {
  apiErrorMessage,
  assignTaskEmployee,
  attachSharePointToTask,
  getRecurringTemplates,
  rollForwardOtherTask,
  unassignTaskEmployee,
  unlinkSharePointFromTask,
  updateOtherTask,
} from "../../../api";
import SharePointFilePicker from "../../../components/SharePointFilePicker";
import ConfettiBurst from "../ConfettiBurst";
import OwnerBubble from "../OwnerBubble";
import OwnerPickerModal from "../OwnerPickerModal";
import {
  formatDue, isBookkeepingTask, isFinished, isOverdue, periodKey, tileCounts, todayIso,
} from "../bookkeeping";
import BookkeepingPanel from "./BookkeepingPanel";
import RecurringTemplatesView from "./RecurringTemplatesView";
import WorkFormModal from "./WorkFormModal";
import { CheckIcon, RepeatsCell, RollForwardIcon } from "./icons";

// The Tasks page's Bookkeeping tab. Each row is one work PERIOD (Nov 2026,
// Jul – Sep 2026) of a piece of bookkeeping, PST or GST work; recurring work
// is driven by a template (see "Recurring templates"), so Q1 can sit waiting
// on the client while Q2 is already in progress, as two rows.

const VIEWS = [
  { key: "queue", label: "Work queue", icon: "☰" },
  { key: "client", label: "By client", icon: "👥" },
  { key: "templates", label: "Recurring templates", icon: "⟳" },
];

const th = {
  textAlign: "left", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.03em",
  color: "#676879", fontWeight: 600, padding: "10px 12px", whiteSpace: "nowrap",
  borderBottom: "1px solid #e6e9ef", background: "#f7f8fa", position: "sticky", top: 0, zIndex: 1,
};
const td = { padding: "9px 12px", borderBottom: "1px solid #f1f3f6", fontSize: 13, verticalAlign: "middle" };
const select = {
  border: "1px solid #d0d4e4", borderRadius: 6, padding: "6px 8px", fontSize: 13,
  background: "#fff", color: "#323338",
};
const iconBtn = {
  border: "none", background: "none", padding: 4, cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 3, color: "#676879", fontSize: 13,
  borderRadius: 4,
};

function Tile({ value, label, color, icon, active, onClick }) {
  return (
    <button
      type="button"
      data-testid={`tile-${label}`}
      onClick={onClick}
      aria-pressed={active}
      title={active ? "Show all open periods" : `Show only: ${label.toLowerCase()}`}
      style={{
        flex: 1, padding: "6px 20px", minWidth: 150, textAlign: "left", cursor: "pointer",
        border: "none", borderRight: "1px solid #e6e9ef", font: "inherit",
        background: active ? "#eef7f1" : "transparent",
        boxShadow: active ? "inset 0 -3px 0 #00854d" : "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 28, fontWeight: 700, color: color || "#323338" }}>{value}</span>
        <span aria-hidden="true" style={{ fontSize: 20 }}>{icon}</span>
      </div>
      <div style={{ fontSize: 13, color: "#323338" }}>{label}</div>
    </button>
  );
}

export default function BookkeepingTab({
  tasks = [], setTasks, taskTypes = [], setTaskTypes, clients = [], employees = [],
  statusOptions = [], loading = false, reload,
}) {
  const [view, setView] = useState("queue");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("open");
  const [periodFilter, setPeriodFilter] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [sort, setSort] = useState({ field: "period", dir: "asc" });
  const [selectedId, setSelectedId] = useState(null);
  const [ownerTask, setOwnerTask] = useState(null);
  const [ownerSaving, setOwnerSaving] = useState(false);
  const [pickerTask, setPickerTask] = useState(null);
  const [filesOpenId, setFilesOpenId] = useState(null);
  const [form, setForm] = useState(null);   // null | { template }
  const [templates, setTemplates] = useState([]);
  const [banner, setBanner] = useState(null);
  const [confetti, setConfetti] = useState(0);
  const [busyIds, setBusyIds] = useState(new Set());

  const today = todayIso();

  const loadTemplates = useCallback(async () => {
    try {
      setTemplates((await getRecurringTemplates()) || []);
    } catch (e) {
      setBanner({ type: "error", msg: apiErrorMessage(e, "Could not load recurring templates.") });
    }
  }, []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  useEffect(() => {
    if (!banner) return undefined;
    const t = setTimeout(() => setBanner(null), 4000);
    return () => clearTimeout(t);
  }, [banner]);

  const reloadAll = useCallback(async () => {
    await Promise.all([reload?.(), loadTemplates()]);
  }, [reload, loadTemplates]);

  const bkTasks = useMemo(
    () => tasks.filter((t) => isBookkeepingTask(t, taskTypes)),
    [tasks, taskTypes]);

  const completedStatus = useMemo(
    () => statusOptions.find((s) => /^completed?$/i.test((s.name || "").trim()))
      || statusOptions.find((s) => /complete/i.test(s.name || "")),
    [statusOptions]);

  const statusById = useMemo(() => {
    const m = {};
    for (const s of statusOptions) m[String(s.id)] = s;
    return m;
  }, [statusOptions]);

  // Filters other than Status feed the tiles, so the tiles describe what
  // the person is looking at (one client, one owner) rather than the firm.
  const scoped = useMemo(() => {
    const q = query.trim().toLowerCase();
    return bkTasks.filter((t) => {
      if (clientFilter && String(t.client) !== String(clientFilter)) return false;
      if (ownerFilter && !(t.assigned_employee_ids || []).map(String).includes(String(ownerFilter))) return false;
      if (periodFilter && (t.period_label || "") !== periodFilter) return false;
      if (q) {
        const owners = (t.assignments || []).map((a) => a.employee_name).join(" ");
        const hay = `${t.client_name || ""} ${t.name || ""} ${t.period_label || ""} ${t.status_name || ""} ${owners}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [bkTasks, clientFilter, ownerFilter, periodFilter, query]);

  const tiles = useMemo(() => tileCounts(scoped, today), [scoped, today]);

  const rows = useMemo(() => {
    const filtered = scoped.filter((t) => {
      if (statusFilter === "open") return !isFinished(t);
      if (statusFilter === "all") return true;
      if (statusFilter === "completed") return isFinished(t);
      if (statusFilter === "overdue") return isOverdue(t, today);
      if (statusFilter === "waiting") return !isFinished(t) && /waiting/i.test(t.status_name || "");
      if (statusFilter === "review") return !isFinished(t) && /review/i.test(t.status_name || "");
      return String(t.status) === statusFilter && !isFinished(t);
    });
    const dir = sort.dir === "asc" ? 1 : -1;
    const key = {
      client: (t) => (t.client_name || "").toLowerCase(),
      item: (t) => (t.name || "").toLowerCase(),
      period: (t) => periodKey(t),
      due: (t) => t.due_date || "9999-99-99",
      status: (t) => (t.status_name || "").toLowerCase(),
    }[sort.field];
    return [...filtered].sort((a, b) => {
      const ka = key(a); const kb = key(b);
      if (ka < kb) return -dir;
      if (ka > kb) return dir;
      return (a.client_name || "").localeCompare(b.client_name || "")
        || periodKey(a).localeCompare(periodKey(b));
    });
  }, [scoped, statusFilter, sort, today]);

  const periodOptions = useMemo(() => {
    const m = new Map();
    for (const t of bkTasks) if (t.period_label) m.set(t.period_label, t.period_start || "");
    return [...m.entries()].sort((a, b) => b[1].localeCompare(a[1])).map(([label]) => label);
  }, [bkTasks]);

  const clientOptions = useMemo(() => {
    const m = new Map();
    for (const t of bkTasks) if (t.client) m.set(t.client, t.client_name || "");
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [bkTasks]);

  const selected = bkTasks.find((t) => t.id === selectedId) || null;
  const otherOpenPeriods = useMemo(() => {
    if (!selected) return [];
    return bkTasks
      .filter((t) => t.id !== selected.id && !isFinished(t) && t.client === selected.client
        && (selected.template ? t.template === selected.template
          : (t.name === selected.name && t.task_type === selected.task_type)))
      .sort((a, b) => periodKey(a).localeCompare(periodKey(b)));
  }, [bkTasks, selected]);

  const markBusy = (id, on) => setBusyIds((prev) => {
    const next = new Set(prev);
    if (on) next.add(id); else next.delete(id);
    return next;
  });

  const patch = async (task, payload) => {
    markBusy(task.id, true);
    try {
      const updated = await updateOtherTask(task.id, payload);
      if (Object.prototype.hasOwnProperty.call(payload, "status")) {
        // Closing a recurring period can make the next one server-side.
        await reload?.();
      } else {
        setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, ...updated } : t)));
      }
      return updated;
    } catch (e) {
      setBanner({ type: "error", msg: apiErrorMessage(e, "Could not save the change.") });
      return null;
    } finally {
      markBusy(task.id, false);
    }
  };

  const complete = async (task) => {
    if (isFinished(task)) return;
    if (!completedStatus) {
      setBanner({ type: "error", msg: "No “Completed” status is set up. Add one in Settings." });
      return;
    }
    const done = await patch(task, { status: completedStatus.id });
    if (done) {
      setConfetti((k) => k + 1);
      setBanner({ type: "ok", msg: `${task.client_name || task.name} · ${task.period_label || task.name} marked complete.` });
    }
  };

  const rollForward = async (task) => {
    markBusy(task.id, true);
    try {
      const res = await rollForwardOtherTask(task.id);
      await reload?.();
      const label = res.task?.period_label || res.task?.name || "next period";
      setBanner({
        type: "ok",
        msg: res.created ? `Rolled forward: ${label} created.` : `${label} already exists.`,
      });
    } catch (e) {
      setBanner({ type: "error", msg: apiErrorMessage(e, "Could not roll forward.") });
    } finally {
      markBusy(task.id, false);
    }
  };

  const saveOwners = async (task, ids) => {
    const before = new Set((task.assigned_employee_ids || []).map(Number));
    const after = new Set(ids.map(Number));
    setOwnerSaving(true);
    try {
      for (const id of before) if (!after.has(id)) await unassignTaskEmployee(task.id, id);
      for (const id of after) if (!before.has(id)) await assignTaskEmployee(task.id, id);
      setOwnerTask(null);
      await reload?.();
    } catch (e) {
      setBanner({ type: "error", msg: apiErrorMessage(e, "Could not change the owner.") });
    } finally {
      setOwnerSaving(false);
    }
  };

  const onFilePicked = async (file) => {
    const task = pickerTask;
    setPickerTask(null);
    if (!task) return;
    try {
      await attachSharePointToTask(task.id, { name: file.name, webUrl: file.webUrl, id: file.id || "" });
      await reload?.();
    } catch (e) {
      setBanner({ type: "error", msg: apiErrorMessage(e, "Could not attach the file.") });
    }
  };

  const unlink = async (fileId) => {
    try {
      await unlinkSharePointFromTask(fileId);
      await reload?.();
    } catch (e) {
      setBanner({ type: "error", msg: apiErrorMessage(e, "Could not remove the file.") });
    }
  };

  const sortHeader = (field, labelText, extra = {}) => (
    <th
      style={{ ...th, cursor: "pointer", ...extra }}
      onClick={() => setSort((s) => ({ field, dir: s.field === field && s.dir === "asc" ? "desc" : "asc" }))}
      aria-sort={sort.field === field ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
    >
      {labelText}{sort.field === field ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
    </th>
  );

  const renderRow = (t) => {
    const status = statusById[String(t.status)];
    const done = isFinished(t);
    const overdue = isOverdue(t, today);
    const files = t.files || [];
    const recurring = t.recurrence && t.recurrence !== "none";
    const busy = busyIds.has(t.id);
    return (
      <tr
        key={t.id}
        onClick={() => setSelectedId(t.id)}
        data-testid={`bk-row-${t.id}`}
        style={{
          cursor: "pointer",
          background: selectedId === t.id ? "#eef7f1" : "transparent",
          opacity: busy ? 0.6 : 1,
        }}
      >
        <td style={{ ...td, fontWeight: 500 }}>{t.client_name || "—"}</td>
        <td style={td}>{t.name}</td>
        <td style={{ ...td, whiteSpace: "nowrap" }}>{t.period_label || "—"}</td>
        <td style={{ ...td, whiteSpace: "nowrap", color: overdue ? "#d83a52" : "#323338", fontWeight: overdue ? 600 : 400 }}>
          {formatDue(t.due_date) || "—"}
        </td>
        <td style={{ ...td, textAlign: "center" }}><RepeatsCell recurrence={t.recurrence} /></td>
        <td style={td} onClick={(e) => e.stopPropagation()}>
          <select
            aria-label="Status"
            value={t.status ?? ""}
            onChange={(e) => patch(t, { status: e.target.value ? Number(e.target.value) : null })}
            style={{
              border: "none", borderRadius: 999, padding: "4px 10px", fontSize: 12, cursor: "pointer",
              background: status?.color || "#e6e9ef", color: status?.color ? "#fff" : "#323338",
              maxWidth: 170,
            }}
          >
            <option value="">No status</option>
            {statusOptions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </td>
        <td style={td} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
            {(t.assignments || []).slice(0, 3).map((a) => (
              <OwnerBubble key={a.id} owner={a.employee_name || ""} size={26} onClick={() => setOwnerTask(t)} />
            ))}
            {(t.assignments || []).length === 0 && (
              <button type="button" onClick={() => setOwnerTask(t)} style={{ ...iconBtn, fontSize: 12 }} title="Assign owner">
                + Owner
              </button>
            )}
          </div>
        </td>
        <td style={{ ...td, whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: "flex", alignItems: "center", gap: 2, position: "relative" }}>
            <button
              type="button" style={iconBtn}
              title={files.length ? `${files.length} attachment${files.length === 1 ? "" : "s"}` : "Attach from SharePoint"}
              aria-label="Attachments"
              onClick={() => (files.length ? setFilesOpenId(filesOpenId === t.id ? null : t.id) : setPickerTask(t))}
            >
              📎{files.length > 0 && <span style={{ fontSize: 11 }}>{files.length}</span>}
            </button>
            {filesOpenId === t.id && (
              <div style={{
                position: "absolute", top: "100%", left: 0, zIndex: 20, background: "#fff",
                border: "1px solid #d0d4e4", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                padding: 8, minWidth: 240,
              }}>
                {files.map((f) => (
                  <div key={f.id} style={{ display: "flex", gap: 8, alignItems: "center", padding: "4px 2px" }}>
                    <a href={f.sharepoint_url} target="_blank" rel="noreferrer"
                       style={{ flex: 1, fontSize: 12, color: "#0073ea", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {f.name}
                    </a>
                    <button type="button" onClick={() => unlink(f.id)} title="Remove"
                            style={{ ...iconBtn, fontSize: 11, color: "#c3c6d4" }}>✕</button>
                  </div>
                ))}
                <button type="button" onClick={() => { setFilesOpenId(null); setPickerTask(t); }}
                        style={{ ...iconBtn, color: "#0073ea", fontSize: 12, marginTop: 4 }}>
                  + Attach from SharePoint
                </button>
              </div>
            )}
            <button
              type="button" style={iconBtn} aria-label="Notes"
              title={t.open_todo_count ? `${t.open_todo_count} open to-do${t.open_todo_count === 1 ? "" : "s"}` : "Notes"}
              onClick={() => setSelectedId(t.id)}
            >
              💬{t.note_count > 0 && (
                <span style={{ fontSize: 11, color: t.open_todo_count ? "#d17a00" : "#676879" }}>{t.note_count}</span>
              )}
            </button>
            <button
              type="button" aria-label={done ? "Completed" : "Mark complete"}
              title={done ? "Completed" : "Mark complete"}
              disabled={done || busy}
              onClick={() => complete(t)}
              className="bk-complete"
              style={{ ...iconBtn, opacity: done ? 1 : 0.85, cursor: done ? "default" : "pointer" }}
            >
              <CheckIcon color={done ? "#00a35c" : "#00c875"} />
            </button>
            {recurring && (
              <button
                type="button" aria-label="Roll forward" title="Roll forward to the next period"
                disabled={busy}
                onClick={() => rollForward(t)}
                style={{ ...iconBtn, color: "#0073ea" }}
              >
                <RollForwardIcon />
              </button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const groupedByClient = useMemo(() => {
    if (view !== "client") return [];
    const m = new Map();
    for (const t of rows) {
      const k = t.client_name || "No client";
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(t);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows, view]);

  return (
    <div style={{ flex: 1, display: "flex", overflow: "hidden", background: "#fff" }}>
      <style>{`.bk-complete:not(:disabled):hover { background: #e5f6ed; }`}</style>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", padding: "12px 24px 0" }}>
        <div style={{ display: "flex", borderBottom: "1px solid #e6e9ef", paddingBottom: 12, marginBottom: 12 }}>
          {[
            { key: "open", value: tiles.active, label: "Active periods", icon: "🗓️" },
            { key: "waiting", value: tiles.waiting, label: "Waiting on client", icon: "⏳" },
            { key: "review", value: tiles.review, label: "Ready for review", icon: "✅" },
            { key: "overdue", value: tiles.overdue, label: "Overdue", icon: "❗",
              color: tiles.overdue ? "#d83a52" : undefined },
          ].map((tile) => (
            <Tile
              key={tile.key} {...tile}
              active={statusFilter === tile.key}
              onClick={() => {
                // Clicking a tile filters to it; clicking it again (or
                // Active periods) goes back to every open period.
                setStatusFilter((cur) => (cur === tile.key ? "open" : tile.key));
                if (view === "templates") setView("queue");
              }}
            />
          ))}
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
          <input
            type="search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="🔍 Search tasks…" aria-label="Search bookkeeping"
            style={{ ...select, width: 220 }}
          />
          <select aria-label="Status filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={select}>
            <option value="open">Status: Open</option>
            <option value="all">Status: All</option>
            <option value="overdue">Overdue</option>
            <option value="waiting">Waiting on client (any)</option>
            <option value="review">Ready for review (any)</option>
            {statusOptions.filter((s) => !/complete/i.test(s.name || "")).map((s) => (
              <option key={s.id} value={String(s.id)}>{s.name}</option>
            ))}
            <option value="completed">Completed</option>
          </select>
          <select aria-label="Period filter" value={periodFilter} onChange={(e) => setPeriodFilter(e.target.value)} style={select}>
            <option value="">Period: All</option>
            {periodOptions.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select aria-label="Owner filter" value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} style={select}>
            <option value="">Owner: All</option>
            {employees.filter((e) => e.is_active !== false).map((e) => (
              <option key={e.id} value={e.id}>{e.name || e.full_name || e.user?.username}</option>
            ))}
          </select>
          <select aria-label="Client filter" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} style={{ ...select, maxWidth: 220 }}>
            <option value="">Client: All</option>
            {clientOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <div style={{ flex: 1 }} />
          <button
            type="button" onClick={() => setForm({ template: null })}
            style={{
              border: "none", background: "#00854d", color: "#fff", borderRadius: 6,
              padding: "7px 14px", fontSize: 13, fontWeight: 500, cursor: "pointer",
            }}
          >+ New task</button>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center" }} role="tablist">
          {VIEWS.map((v) => (
            <button
              key={v.key} type="button" role="tab" aria-selected={view === v.key}
              onClick={() => setView(v.key)}
              style={{
                borderRadius: 6, padding: "6px 12px", fontSize: 13, cursor: "pointer",
                border: view === v.key ? "1px solid #00854d" : "1px solid #d0d4e4",
                background: view === v.key ? "#00854d" : "#fff",
                color: view === v.key ? "#fff" : "#323338",
              }}
            >
              <span aria-hidden="true">{v.icon}</span> {v.label}
            </button>
          ))}
          {banner && (
            <span role="status" style={{ marginLeft: 12, fontSize: 13, color: banner.type === "error" ? "#d83a52" : "#00854d" }}>
              {banner.msg}
            </span>
          )}
        </div>

        {view === "templates" ? (
          <RecurringTemplatesView
            templates={templates} employees={employees} query={query}
            clientFilter={clientFilter} ownerFilter={ownerFilter}
            onEdit={(t) => setForm({ template: t })}
            onChanged={reloadAll}
          />
        ) : (
          <div style={{ flex: 1, overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
              <thead>
                <tr>
                  {sortHeader("client", "Client")}
                  {sortHeader("item", "Work item")}
                  {sortHeader("period", "Period")}
                  {sortHeader("due", "Due")}
                  <th style={{ ...th, textAlign: "center" }}>Repeats</th>
                  {sortHeader("status", "Status")}
                  <th style={th}>Owner</th>
                  <th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && rows.length === 0 && (
                  <tr><td colSpan={8} style={{ ...td, color: "#9699a6" }}>Loading…</td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={8} style={{ ...td, color: "#9699a6" }}>Nothing matches these filters.</td></tr>
                )}
                {view === "queue"
                  ? rows.map(renderRow)
                  : groupedByClient.map(([client, list]) => [
                    <tr key={`h-${client}`}>
                      <td colSpan={8} style={{ ...td, background: "#f7f8fa", fontWeight: 600 }}>
                        {client} <span style={{ color: "#676879", fontWeight: 400 }}>· {list.length} period{list.length === 1 ? "" : "s"}</span>
                      </td>
                    </tr>,
                    ...list.map(renderRow),
                  ])}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && view !== "templates" && (
        <BookkeepingPanel
          key={selected.id}
          task={selected}
          statusOptions={statusOptions}
          otherOpenPeriods={otherOpenPeriods}
          onSelectTask={(t) => setSelectedId(t.id)}
          onPatch={patch}
          onEditOwners={(t) => setOwnerTask(t)}
          onClose={() => setSelectedId(null)}
          onNotesChanged={() => reload?.()}
          onRepeatsChanged={reloadAll}
        />
      )}

      {ownerTask && (
        <OwnerPickerModal
          employees={employees}
          initialIds={ownerTask.assigned_employee_ids || []}
          busy={ownerSaving}
          onSave={(ids) => saveOwners(ownerTask, ids)}
          onClose={() => setOwnerTask(null)}
        />
      )}

      {pickerTask && (
        <SharePointFilePicker
          isOpen={true}
          clientName={pickerTask.client_name || pickerTask.name}
          onClose={() => setPickerTask(null)}
          onFileSelected={onFilePicked}
        />
      )}

      {form && (
        <WorkFormModal
          clients={clients} employees={employees} taskTypes={taskTypes} setTaskTypes={setTaskTypes}
          template={form.template}
          onClose={() => setForm(null)}
          onSaved={reloadAll}
        />
      )}

      {confetti > 0 && <ConfettiBurst key={confetti} onDone={() => setConfetti(0)} />}
    </div>
  );
}
